import { Box, FormControlLabel, InputLabel, Typography } from '@mui/material';
import { DIFFERENT_COLOR, TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import PieChartCommon from 'components/Common/PieChartCommon';
import SkeletonCharts from 'components/Common/SkeletonCharts';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import { useClientSettingsStyle } from 'components/Dashboard/ClientSettings/ClientSettingsStyle';
import { QUERY_KEY } from 'constants/constants';
import { get, isEmpty } from 'lodash';
import randomColor from 'randomcolor';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  getElevationFieldAnalytic,
  getSizeElevationFieldAnalytic,
} from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { changeSizeElevationFieldList, mapViewSelector, setDataElevationField } from 'store/slices/mapViewSlice';
import ElevationFieldCardLayer from '../../Layer/ElevationField';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import InfoTable from '../CropCover/InfoTable';

const ElevationFieldAnalytics = () => {
  const dispatch = useAppDispatch();
  const [chartMode, setChartMode] = useState(false);
  const classes = useClientSettingsStyle();

  const { analyticId, sizeElevationFieldSelected, dataElevationField, sizeElevationFieldList } =
    useAppSelector(mapViewSelector);

  useQuery(
    [QUERY_KEY.SIZE_ELEVATION_FIELD_ANALYTIC, analyticId],
    () => getSizeElevationFieldAnalytic(analyticId || ''),
    {
      enabled: !!analyticId && isEmpty(sizeElevationFieldList),
      onSuccess: (response) => {
        if (response.data) {
          dispatch(changeSizeElevationFieldList(response.data));
        }
      },
    }
  );

  const { data: dataCropType } = useQuery(
    [QUERY_KEY.ELEVATION_FIELD_ANALYTIC, analyticId, sizeElevationFieldSelected],
    () => getElevationFieldAnalytic(analyticId || '', sizeElevationFieldSelected + ''),
    { enabled: !!analyticId && !!sizeElevationFieldSelected }
  );

  const dataPie = useMemo(() => {
    if (dataElevationField) {
      return dataElevationField.map((_item: any) => ({
        name: _item.type,
        value: _item.lduseArea,
        totalTree: _item.totalTree,
      }));
    }
    return [];
  }, [dataElevationField]);

  const getDataChart = useMemo(
    () =>
      dataElevationField?.map((_item: any) => ({
        name: _item.type,
        value: chartMode ? _item.totalTree : _item.lduseArea,
      })),
    [chartMode, dataElevationField]
  );

  const hasTreeCount = !!(dataElevationField?.length && dataElevationField.some((item: any) => item.totalTree > 0));

  const colorElevationField = useMemo(() => {
    if (dataCropType?.data) {
      let colors: string[] = [];
      dispatch(
        setDataElevationField(
          dataCropType.data.map((item: any, index: number) => {
            const colorRandom = randomColor({ luminosity: 'dark' });
            const _color = item.color || get(DIFFERENT_COLOR, `[${index}]`, colorRandom);
            colors.push(_color);
            return {
              ...item,
              visible: true,
              color: _color,
            };
          })
        )
      );
      return colors;
    }
    return [];
  }, [dataCropType, dispatch]);

  return (
    <SwipeableViewsCustom
      isDisableLabel
      analyticName={TYPE_ANALYTICS_MAP_VIEW.ELEVATION_FIELD}
      analyticTab={
        <>
          {dataElevationField?.length > 0 ? (
            <>
              <PieChartCommon
                title="Elevation Field"
                dataChart={getDataChart}
                colorCharts={colorElevationField}
                withoutConvertMultiLanguage
              />
              {hasTreeCount && (
                <>
                  <InputLabel sx={{ marginLeft: '25px', fontSize: '14px' }}>Chart Mode:</InputLabel>
                  <FormControlLabel
                    sx={{ marginLeft: '30px' }}
                    control={
                      <SwitchCustom
                        checked={chartMode}
                        onClick={() => {
                          setChartMode(!chartMode);
                        }}
                      />
                    }
                    label={<Typography className={classes.textSetting}>{chartMode ? 'Tree Count' : 'Area'}</Typography>}
                  />
                </>
              )}
              <Box component={'div'} sx={{ marginTop: '30px' }}>
                <InfoTable dataPie={dataPie} hasTreeCount={hasTreeCount} withoutConvertMultiLanguageTableLabel />
              </Box>
            </>
          ) : (
            <>
              <SkeletonCharts count={1} />
            </>
          )}
        </>
      }
      layerTab={<ElevationFieldCardLayer labelCard={TYPE_ANALYTICS_MAP_VIEW.ELEVATION_FIELD} />}
      isShowDownloadButton={dataElevationField?.length > 0}
    />
  );
};

export default ElevationFieldAnalytics;
